import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { apiErr, API_BASE, getStoredToken } from "@/lib/api";
import {
  Upload, ScanLine, Loader2, Sparkles, ImageIcon, FileSpreadsheet, ArrowRight,
  Hammer, Wrench, Zap, Paintbrush, LayoutGrid, CheckCircle2, RotateCcw, Sofa, Lightbulb, Armchair,
  Images, X, Ruler, Move3d,
} from "lucide-react";
import PanoramaViewer from "@/components/three/PanoramaViewer";

const CAT_STYLE = {
  Demolition: { icon: Hammer, cls: "bg-red-500/15 text-red-400 border-red-500/30" },
  Drywall: { icon: LayoutGrid, cls: "bg-slate-500/15 text-slate-300 border-slate-500/30" },
  Plumbing: { icon: Wrench, cls: "bg-sky-500/15 text-sky-400 border-sky-500/30" },
  Electrical: { icon: Zap, cls: "bg-amber-500/15 text-amber-400 border-amber-500/30" },
  Flooring: { icon: LayoutGrid, cls: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30" },
  Millwork: { icon: Hammer, cls: "bg-orange-500/15 text-orange-400 border-orange-500/30" },
  Painting: { icon: Paintbrush, cls: "bg-purple-500/15 text-purple-400 border-purple-500/30" },
  HVAC: { icon: Zap, cls: "bg-cyan-500/15 text-cyan-400 border-cyan-500/30" },
  Tiling: { icon: LayoutGrid, cls: "bg-teal-500/15 text-teal-400 border-teal-500/30" },
  Fixtures: { icon: Wrench, cls: "bg-blue-500/15 text-blue-400 border-blue-500/30" },
};

const fileUrl = (path) => `${API_BASE}/files/${path}?auth=${encodeURIComponent(getStoredToken())}`;

function ExploreTour({ remodel, onClose, onGenerated }) {
  const vps = [];
  if (remodel.panorama_path) vps.push({ key: "pano", label: "360° Tour", url: fileUrl(remodel.panorama_path), equirect: true });
  if (remodel.after_image_path) vps.push({ key: "after", label: "After", url: fileUrl(remodel.after_image_path), equirect: false });
  if (remodel.staged_image_path) vps.push({ key: "staged", label: "Staged", url: fileUrl(remodel.staged_image_path), equirect: false });
  const [active, setActive] = useState(vps[0]?.key);
  const [busy, setBusy] = useState(false);
  const cur = vps.find((v) => v.key === active) || vps[0];

  const genPano = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/remodels/${remodel.id}/panorama`);
      toast.success("360° panorama ready");
      onGenerated(data);
      setActive("pano");
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/95 flex flex-col" data-testid="explore-modal">
      <div className="flex items-center justify-between px-5 py-3 border-b border-white/10">
        <div className="flex items-center gap-2 text-white">
          <Move3d className="h-5 w-5 text-primary" />
          <span className="font-semibold">Explore Room</span>
          <span className="text-sm text-white/50">· {cur?.label}</span>
        </div>
        <button data-testid="explore-close" onClick={onClose} className="rounded-full border border-white/20 bg-white/10 p-2 text-white hover:bg-white/20"><X className="h-5 w-5" /></button>
      </div>
      <div className="flex-1 relative">
        {cur && <PanoramaViewer imageUrl={cur.url} equirect={cur.equirect} />}
        <div className="absolute top-4 left-1/2 -translate-x-1/2 flex items-center gap-2" data-testid="viewpoint-switcher">
          {vps.map((v) => (
            <button key={v.key} data-testid={`viewpoint-${v.key}`} onClick={() => setActive(v.key)}
              className={`rounded-full px-3 py-1.5 text-xs font-semibold backdrop-blur transition-colors ${active === v.key ? "bg-primary text-primary-foreground" : "bg-white/10 text-white hover:bg-white/20"}`}>
              {v.label}
            </button>
          ))}
          {!remodel.panorama_path && (
            <button data-testid="generate-360-btn" onClick={genPano} disabled={busy}
              className="inline-flex items-center gap-1.5 rounded-full bg-primary text-primary-foreground px-3 py-1.5 text-xs font-bold disabled:opacity-60">
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />} Generate 360°
            </button>
          )}
        </div>
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 rounded-full bg-black/60 text-white/80 px-4 py-1.5 text-xs backdrop-blur pointer-events-none" data-testid="explore-hint">
          {cur?.equirect ? "Full 360° — drag to look anywhere · scroll to zoom" : "Drag to look around · scroll to zoom · Generate 360° for a full tour"}
        </div>
      </div>
    </div>
  );
}


export default function RoomRemodel({ projectId = null, embedded = false }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const inputRef = useRef();
  const [remodel, setRemodel] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState("");
  const [view, setView] = useState("studio");
  const [lightbox, setLightbox] = useState(null);
  const [explore, setExplore] = useState(null);
  const [details, setDetails] = useState("");

  useEffect(() => { setDetails(remodel?.details || ""); }, [remodel?.id]);

  const saveDetails = async () => {
    if (!remodel || (details || "") === (remodel.details || "")) return;
    try {
      const { data } = await api.post(`/remodels/${remodel.id}/details`, { details });
      setRemodel(data);
      qc.invalidateQueries({ queryKey: ["remodels", projectId] });
      toast.success("Room details saved");
    } catch (e) { toast.error(apiErr(e)); }
  };

  const { data: history = [] } = useQuery({ queryKey: ["remodels", projectId], queryFn: async () => (await api.get(`/remodels${projectId ? `?project_id=${projectId}` : ""}`)).data });

  const reset = () => { setRemodel(null); setPreview(null); };

  const onFile = async (file) => {
    if (!file) return;
    if (!/(jpe?g|png|webp)$/i.test(file.name)) { toast.error("Use a JPG, PNG or WebP image."); return; }
    setPreview(URL.createObjectURL(file));
    setBusy("upload");
    const fd = new FormData();
    fd.append("file", file);
    fd.append("name", file.name.replace(/\.[^.]+$/, "") || "Room Remodel");
    if (projectId) fd.append("project_id", projectId);
    try {
      const { data } = await api.post("/remodels/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setRemodel(data);
      qc.invalidateQueries({ queryKey: ["remodels", projectId] });
      toast.success("Photo uploaded");
    } catch (e) { toast.error(apiErr(e)); setPreview(null); } finally { setBusy(""); }
  };

  const analyze = async () => {
    setBusy("analyze");
    try {
      const { data } = await api.post(`/remodels/${remodel.id}/analyze`);
      setRemodel(data);
      toast.success("Room analysed");
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(""); }
  };
  const visualize = async () => {
    setBusy("visualize");
    try {
      const { data } = await api.post(`/remodels/${remodel.id}/visualize`);
      setRemodel(data);
      toast.success("'After' concept generated");
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(""); }
  };
  const genBoq = async () => {
    setBusy("boq");
    try {
      const { data } = await api.post(`/remodels/${remodel.id}/generate-boq`);
      qc.invalidateQueries({ queryKey: ["projects"] });
      if (embedded) {
        qc.invalidateQueries({ queryKey: ["boq", projectId] });
        qc.invalidateQueries({ queryKey: ["remodels", projectId] });
        toast.success("Priced BOQ added to this project — open the Estimate tab to review it.");
      } else {
        toast.success("Priced BOQ created");
        navigate(`/projects/${data.project_id}`);
      }
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(""); }
  };
  const suggest = async () => {
    setBusy("suggest");
    try {
      const { data } = await api.post(`/remodels/${remodel.id}/suggest`);
      setRemodel(data);
      toast.success("Design ideas ready");
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(""); }
  };
  const stage = async () => {
    setBusy("stage");
    try {
      const { data } = await api.post(`/remodels/${remodel.id}/stage`);
      setRemodel(data);
      toast.success("Furnished room staged");
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(""); }
  };

  const beforeSrc = preview || (remodel?.photo_path ? fileUrl(remodel.photo_path) : null);

  return (
    <div className={embedded ? "" : "max-w-7xl mx-auto"}>
      <div className="flex items-center justify-between mb-6">
        {embedded ? (
          <div>
            <h2 className="text-lg font-semibold flex items-center gap-2"><ScanLine className="h-5 w-5 text-primary" /> Room Remodel</h2>
            <p className="text-xs text-muted-foreground mt-0.5">Photo → AI checklist → 'after' concept → one-tap priced BOQ, all saved to this project.</p>
          </div>
        ) : (
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-2"><ScanLine className="h-6 w-6 text-primary" /> Room Remodel</h1>
            <p className="text-sm text-muted-foreground mt-1">Photo → AI checklist → 'after' concept → one-tap priced BOQ.</p>
          </div>
        )}
        {view === "studio" && remodel && <button onClick={reset} data-testid="remodel-reset-btn" className="inline-flex items-center gap-2 rounded-lg border border-border px-4 py-2 font-semibold hover:bg-secondary transition-colors"><RotateCcw className="h-4 w-4" /> New</button>}
      </div>

      <div className="inline-flex rounded-lg border border-border p-1 mb-6" data-testid="remodel-view-tabs">
        {[["studio", "Studio", ScanLine], ["photos", "Photos", Images]].map(([v, label, Icon]) => (
          <button key={v} data-testid={`remodel-tab-${v}`} onClick={() => setView(v)}
            className={`inline-flex items-center gap-2 rounded-md px-4 py-1.5 text-sm font-semibold transition-colors ${view === v ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>
            <Icon className="h-4 w-4" /> {label}
          </button>
        ))}
      </div>

      {view === "studio" && (<>
      {!remodel ? (
        <div
          data-testid="remodel-dropzone"
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => { e.preventDefault(); onFile(e.dataTransfer.files?.[0]); }}
          className="rounded-2xl border-2 border-dashed border-border hover:border-primary/60 transition-colors cursor-pointer py-24 text-center blueprint-grid"
        >
          <input ref={inputRef} type="file" accept="image/*" className="hidden" data-testid="remodel-file-input" onChange={(e) => onFile(e.target.files?.[0])} />
          {busy === "upload" ? <Loader2 className="h-10 w-10 mx-auto text-primary animate-spin" /> : <Upload className="h-10 w-10 mx-auto text-primary" />}
          <p className="mt-4 font-semibold text-lg">Drop a room photo here</p>
          <p className="text-sm text-muted-foreground">or click to browse · JPG, PNG, WebP</p>
        </div>
      ) : (
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Before / After */}
          <div className="space-y-6">
            <div className="rounded-xl border border-border bg-card overflow-hidden" data-testid="before-card">
              <div className="px-4 py-2.5 border-b border-border flex items-center gap-2 text-xs font-mono uppercase tracking-wider text-muted-foreground"><ImageIcon className="h-3.5 w-3.5" /> Before</div>
              {beforeSrc && <img src={beforeSrc} alt="Room before" className="w-full h-64 object-cover" />}
            </div>
            <div className="rounded-xl border border-border bg-card overflow-hidden" data-testid="after-card">
              <div className="px-4 py-2.5 border-b border-border flex items-center gap-2 text-xs font-mono uppercase tracking-wider text-muted-foreground"><Sparkles className="h-3.5 w-3.5 text-primary" /> AI 'After' Concept</div>
              {remodel.after_image_path ? (
                <div className="relative group">
                  <img src={fileUrl(remodel.after_image_path)} alt="Room after" className="w-full h-64 object-cover" />
                  <button data-testid="explore-after-btn" onClick={() => setExplore(remodel)}
                    className="absolute bottom-3 right-3 inline-flex items-center gap-1.5 rounded-full bg-black/70 text-white px-3 py-1.5 text-xs font-semibold backdrop-blur hover:bg-primary transition-colors">
                    <Move3d className="h-3.5 w-3.5" /> Explore Room
                  </button>
                </div>
              ) : (
                <div className="h-64 grid place-items-center text-sm text-muted-foreground">
                  {busy === "visualize" ? <div className="text-center"><Loader2 className="h-7 w-7 mx-auto animate-spin text-primary mb-2" />Rendering concept…</div> : "Run Visualize to generate"}
                </div>
              )}
            </div>
            {(remodel.staged_image_path || busy === "stage") && (
              <div className="rounded-xl border border-border bg-card overflow-hidden" data-testid="staged-card">
                <div className="px-4 py-2.5 border-b border-border flex items-center gap-2 text-xs font-mono uppercase tracking-wider text-muted-foreground"><Sofa className="h-3.5 w-3.5 text-primary" /> Fully Furnished Staging</div>
                {remodel.staged_image_path ? (
                  <div className="relative group">
                    <img src={fileUrl(remodel.staged_image_path)} alt="Furnished room" className="w-full h-64 object-cover" />
                    <button data-testid="explore-staged-btn" onClick={() => setExplore(remodel)}
                      className="absolute bottom-3 right-3 inline-flex items-center gap-1.5 rounded-full bg-black/70 text-white px-3 py-1.5 text-xs font-semibold backdrop-blur hover:bg-primary transition-colors">
                      <Move3d className="h-3.5 w-3.5" /> Explore Room
                    </button>
                  </div>
                ) : (
                  <div className="h-64 grid place-items-center text-sm text-muted-foreground"><div className="text-center"><Loader2 className="h-7 w-7 mx-auto animate-spin text-primary mb-2" />Staging furniture…</div></div>
                )}
              </div>
            )}
          </div>

          {/* Analysis + actions */}
          <div className="space-y-5">
            <div className="rounded-xl border border-border bg-card p-4" data-testid="room-details-card">
              <label className="flex items-center gap-2 text-sm font-semibold mb-2">
                <Ruler className="h-4 w-4 text-primary" /> Room details & specs <span className="text-xs font-normal text-muted-foreground">(optional)</span>
              </label>
              <p className="text-xs text-muted-foreground mb-2">Add dimensions, materials, fixtures or finish preferences. The AI uses these when analysing, visualising and pricing.</p>
              <textarea
                data-testid="room-details-input"
                value={details}
                onChange={(e) => setDetails(e.target.value)}
                onBlur={saveDetails}
                rows={4}
                placeholder="e.g. 12' x 14', 9ft ceilings. Keep existing window. Quartz counters, matte-black fixtures, LVP flooring, walk-in shower replacing tub."
                className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm resize-y focus:outline-none focus:ring-2 focus:ring-primary/40"
              />
              <div className="mt-2 flex justify-end">
                <button data-testid="save-details-btn" onClick={saveDetails} disabled={(details || "") === (remodel.details || "")}
                  className="rounded-lg bg-primary text-primary-foreground px-3 py-1.5 text-xs font-semibold disabled:opacity-50">Save details</button>
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              <button data-testid="analyze-btn" onClick={analyze} disabled={!!busy}
                className="inline-flex items-center gap-2 rounded-lg bg-primary text-primary-foreground px-4 py-2.5 font-bold hover:bg-orange-600 transition-colors disabled:opacity-60">
                {busy === "analyze" ? <Loader2 className="h-4 w-4 animate-spin" /> : <ScanLine className="h-4 w-4" />} Analyse Room
              </button>
              <button data-testid="visualize-btn" onClick={visualize} disabled={!!busy || !remodel.checklist}
                className="inline-flex items-center gap-2 rounded-lg border border-border px-4 py-2.5 font-semibold hover:bg-secondary transition-colors disabled:opacity-50">
                {busy === "visualize" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Visualise
              </button>
              <button data-testid="generate-boq-btn" onClick={genBoq} disabled={!!busy || !remodel.checklist}
                className="inline-flex items-center gap-2 rounded-lg border border-border px-4 py-2.5 font-semibold hover:bg-secondary transition-colors disabled:opacity-50">
                {busy === "boq" ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4" />} Generate BOQ <ArrowRight className="h-3.5 w-3.5" />
              </button>
              <button data-testid="suggest-btn" onClick={suggest} disabled={!!busy}
                className="inline-flex items-center gap-2 rounded-lg border border-border px-4 py-2.5 font-semibold hover:bg-secondary transition-colors disabled:opacity-60">
                {busy === "suggest" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lightbulb className="h-4 w-4" />} Design Ideas
              </button>
              <button data-testid="stage-btn" onClick={stage} disabled={!!busy}
                className="inline-flex items-center gap-2 rounded-lg border border-border px-4 py-2.5 font-semibold hover:bg-secondary transition-colors disabled:opacity-60">
                {busy === "stage" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sofa className="h-4 w-4" />} Stage Furnished
              </button>
            </div>

            {remodel.summary && (
              <div className="rounded-xl border border-border bg-card p-5" data-testid="analysis-summary">
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-xs font-mono uppercase tracking-wider text-primary">{remodel.room_type}</span>
                  {remodel.estimated_area_sf && <span className="text-xs font-mono text-muted-foreground">≈ {remodel.estimated_area_sf} sf</span>}
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed">{remodel.summary}</p>
              </div>
            )}

            {remodel.checklist && (
              <div className="rounded-xl border border-border bg-card p-5" data-testid="vision-checklist">
                <h3 className="font-semibold mb-3 flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-primary" /> Vision Checklist</h3>
                <div className="space-y-2.5 max-h-[360px] overflow-y-auto">
                  {remodel.checklist.map((c, i) => {
                    const st = CAT_STYLE[c.category] || CAT_STYLE.Demolition;
                    const Icon = st.icon;
                    return (
                      <div key={i} className="flex items-start gap-3" data-testid={`checklist-item-${i}`}>
                        <span className={`shrink-0 inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded border ${st.cls}`}><Icon className="h-3 w-3" />{c.category}</span>
                        <div className="text-sm">
                          <div className="font-medium">{c.item}</div>
                          {c.note && <div className="text-xs text-muted-foreground">{c.note}</div>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {remodel.design_suggestions && (
              <div className="rounded-xl border border-border bg-card p-5 space-y-4" data-testid="design-suggestions">
                <div className="flex items-center gap-2">
                  <Lightbulb className="h-4 w-4 text-primary" />
                  <h3 className="font-semibold">Design Ideas</h3>
                  {remodel.design_suggestions.style && <span className="text-xs font-mono text-muted-foreground">· {remodel.design_suggestions.style}</span>}
                </div>

                {(remodel.design_suggestions.color_palette || []).length > 0 && (
                  <div className="flex items-center gap-2" data-testid="color-palette">
                    {remodel.design_suggestions.color_palette.map((c, i) => (
                      <span key={i} className="h-6 w-6 rounded-full border border-border shadow-inner" style={{ backgroundColor: c }} title={c} />
                    ))}
                  </div>
                )}

                {(remodel.design_suggestions.layout_suggestions || []).length > 0 && (
                  <div className="space-y-2">
                    <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground flex items-center gap-1"><LayoutGrid className="h-3 w-3" /> Layout</div>
                    {remodel.design_suggestions.layout_suggestions.map((l, i) => (
                      <div key={i} className="text-sm" data-testid={`layout-suggestion-${i}`}>
                        <div className="font-medium">{l.title}</div>
                        {l.detail && <div className="text-xs text-muted-foreground">{l.detail}</div>}
                      </div>
                    ))}
                  </div>
                )}

                {(remodel.design_suggestions.furniture || []).length > 0 && (
                  <div className="space-y-2">
                    <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground flex items-center gap-1"><Armchair className="h-3 w-3" /> Furniture Ideas</div>
                    <div className="grid sm:grid-cols-2 gap-2">
                      {remodel.design_suggestions.furniture.map((f, i) => (
                        <div key={i} className="rounded-lg border border-border/60 p-3" data-testid={`furniture-item-${i}`}>
                          <div className="flex items-center justify-between gap-2">
                            <span className="font-medium text-sm">{f.item}</span>
                            {f.approx_price_usd ? <span className="text-xs font-mono text-primary">${Number(f.approx_price_usd).toLocaleString()}</span> : null}
                          </div>
                          {f.style && <div className="text-[11px] text-muted-foreground">{f.style}</div>}
                          {f.why && <div className="text-xs text-muted-foreground mt-1">{f.why}</div>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* History */}
      {!remodel && history.length > 0 && (
        <div className="mt-10">
          <h2 className="text-lg font-semibold mb-4">Recent remodels</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {history.map((r) => (
              <div key={r.id} className="rounded-xl border border-border bg-card overflow-hidden cursor-pointer hover:border-primary/50 transition-colors" onClick={() => setRemodel(r)} data-testid={`remodel-history-${r.id}`}>
                <img src={fileUrl(r.after_image_path || r.photo_path)} alt="" className="w-full h-32 object-cover" />
                <div className="p-3">
                  <div className="font-medium text-sm truncate">{r.name}</div>
                  <div className="text-xs text-muted-foreground capitalize">{r.status}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      </>)}

      {view === "photos" && (
        <div data-testid="remodel-photos-view">
          {history.length === 0 ? (
            <div className="rounded-2xl border-2 border-dashed border-border py-24 text-center text-muted-foreground">
              <Images className="h-10 w-10 mx-auto mb-3 opacity-60" />
              <p className="font-semibold">No photos yet</p>
              <p className="text-sm">Upload a room photo in the Studio to start building your photo library.</p>
            </div>
          ) : (
            <div className="space-y-8">
              {history.map((r) => {
                const shots = [
                  r.photo_path && { path: r.photo_path, label: "Before", Icon: ImageIcon },
                  r.after_image_path && { path: r.after_image_path, label: "AI After", Icon: Sparkles },
                  r.staged_image_path && { path: r.staged_image_path, label: "Staged", Icon: Sofa },
                ].filter(Boolean);
                if (shots.length === 0) return null;
                return (
                  <div key={r.id} data-testid={`photos-group-${r.id}`}>
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <h3 className="font-semibold">{r.name}</h3>
                        {r.room_type && <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">{r.room_type}</span>}
                      </div>
                      {r.created_at && <span className="text-xs text-muted-foreground">{new Date(r.created_at).toLocaleDateString()}</span>}
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                      {shots.map((s) => {
                        const url = fileUrl(s.path);
                        return (
                          <button key={s.path} onClick={() => setLightbox({ url, label: `${r.name} · ${s.label}` })}
                            className="group text-left rounded-xl border border-border bg-card overflow-hidden hover:border-primary/60 transition-colors"
                            data-testid={`photo-${r.id}-${s.label.toLowerCase().replace(/\s+/g, "-")}`}>
                            <div className="relative">
                              <img src={url} alt={s.label} className="w-full h-40 object-cover group-hover:scale-[1.02] transition-transform" />
                              <span className="absolute top-2 left-2 inline-flex items-center gap-1 rounded-full bg-background/80 backdrop-blur px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider border border-border">
                                <s.Icon className="h-3 w-3 text-primary" /> {s.label}
                              </span>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {explore && (
        <ExploreTour remodel={explore} onClose={() => setExplore(null)}
          onGenerated={(r) => { setExplore(r); setRemodel(r); }} />
      )}
    </div>
  );
}
